import { Box, ButtonBase, useTheme } from "@mui/material";

export type KeyValueChipColor =
	| "primary"
	| "secondary"
	| "success"
	| "warning"
	| "error"
	| "info"
	| "board";

export interface KeyValueChipProps {
	label: string;
	value: string | number;
	selected?: boolean;
	color?: KeyValueChipColor;
	/** Overrides the theme color entirely — e.g. a per-assignment chart color */
	customColor?: string;
	/** Which side should be solid - "left" or "right" (default: "right") */
	solidSide?: "left" | "right";
	onClick?: () => void;
}

/**
 * A two-part chip with label on one side and value on the other.
 * One side is outlined, the other is solid (filled).
 *
 * Example: [All] [23] or [Status] [Active]
 */
export const KeyValueChip = ({
	label,
	value,
	selected = false,
	color = "primary",
	customColor,
	solidSide = "right",
	onClick,
}: KeyValueChipProps) => {
	const theme = useTheme();

	// Get the main color - handle "board" specially as it only has .main
	const getMainColor = (): string => {
		if (customColor) return customColor;
		if (color === "board") {
			return theme.palette.board.main;
		}
		return theme.palette[color].main;
	};

	const mainColor = getMainColor();

	// Use same color for border and solid bg (consistent whether selected or not)
	const chipColor = selected ? mainColor : theme.palette.grey[500];
	const solidText = selected
		? theme.palette.getContrastText(mainColor)
		: theme.palette.getContrastText(theme.palette.grey[500]);

	const outlinedSx = {
		border: `1px solid ${chipColor}`,
		color: chipColor,
		bgcolor: "transparent",
	};

	const solidSx = {
		border: `1px solid ${chipColor}`,
		bgcolor: chipColor,
		color: solidText,
	};

	const leftSx = solidSide === "left" ? solidSx : outlinedSx;
	const rightSx = solidSide === "right" ? solidSx : outlinedSx;

	// Use same borderRadius as MuiChip from theme (defaults to 4px)
	const chipRadius =
		(theme.components?.MuiChip?.styleOverrides?.root as { borderRadius?: number })
			?.borderRadius ?? 4;

	const rootSx = {
		display: "inline-flex",
		cursor: onClick ? "pointer" : "default",
		borderRadius: `${chipRadius}px`,
		overflow: "hidden",
		fontFamily: "inherit",
		fontSize: "0.8125rem",
		fontWeight: 500,
		lineHeight: 1.4,
		transition: "all 0.15s ease",
		"&:hover": onClick
			? {
					opacity: 0.85,
				}
			: undefined,
		"&:focus-visible": { outline: `2px solid ${mainColor}`, outlineOffset: 2 },
	};

	const parts = (
		<>
			<Box
				sx={{
					px: 1,
					py: 0.25,
					borderRadius: `${chipRadius}px 0 0 ${chipRadius}px`,
					...leftSx,
					// Remove right border to connect seamlessly
					borderRight: "none",
				}}
				data-component="key-value-chip-label"
			>
				{label}
			</Box>
			<Box
				sx={{
					px: 0.75,
					py: 0.25,
					borderRadius: `0 ${chipRadius}px ${chipRadius}px 0`,
					minWidth: "1.5em",
					textAlign: "center",
					...rightSx,
				}}
				data-component="key-value-chip-value"
			>
				{value}
			</Box>
		</>
	);

	return onClick ? (
		<ButtonBase
			disableRipple
			onClick={onClick}
			sx={rootSx}
			data-component="key-value-chip"
		>
			{parts}
		</ButtonBase>
	) : (
		<Box sx={rootSx} data-component="key-value-chip">
			{parts}
		</Box>
	);
};
